import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useLang } from "@/hooks/use-language";
import { usePermissions, type StaffRole } from "@/hooks/use-permissions";
import {
  listStaff,
  inviteStaff,
  setStaffRoles,
} from "@/lib/admin/team.functions";
import { listAuditLog } from "@/lib/admin/audit.functions";
import { toast } from "sonner";
import { Lock, UserPlus, Shield, Clock, Eye, EyeOff, Copy, RefreshCw, LogIn } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { BackButton } from "@/components/BackButton";

function maskEmail(email: string | null): string {
  if (!email) return "—";
  const [local, domain] = email.split("@");
  if (!domain) return "•••";
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"•".repeat(Math.max(3, local.length - 2))}@${domain}`;
}

const PLATFORM_OWNER_ID = "af09e839-198c-4d7d-8220-0207eebc280c";

export const Route = createFileRoute("/_authenticated/app/admin/team")({
  component: TeamPage,
});

const ROLE_OPTIONS: { value: StaffRole; en: string; ar: string; desc: string }[] = [
  { value: "super_admin", en: "Super Admin", ar: "مدير عام", desc: "Full access incl. roles" },
  { value: "content_manager", en: "Content Manager", ar: "إدارة المحتوى", desc: "Create / edit / publish content" },
  { value: "moderator", en: "Moderator", ar: "مراجع", desc: "Publish / archive / review messages" },
  { value: "support", en: "Support", ar: "دعم", desc: "Read users + contact messages" },
  { value: "finance", en: "Finance", ar: "مالية", desc: "Subscriptions, invoices, refunds" },
];

type Staff = {
  user_id: string;
  email: string | null;
  roles: StaffRole[];
  last_sign_in: string | null;
};

type AuditRow = {
  id: string;
  actor_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  created_at: string;
};

function generatePassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const sym = "!@#$%&*";
  const all = upper + lower + digits + sym;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  const arr = [pick(upper), pick(lower), pick(digits), pick(sym)];
  for (let i = 0; i < 12; i++) arr.push(pick(all));
  return arr.sort(() => Math.random() - 0.5).join("");
}

function TeamPage() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const perms = usePermissions();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const list = useServerFn(listStaff);
  const invite = useServerFn(inviteStaff);
  const setRoles = useServerFn(setStaffRoles);
  const audit = useServerFn(listAuditLog);

  const [staff, setStaff] = useState<Staff[]>([]);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(() => generatePassword());
  const [showPw, setShowPw] = useState(false);
  const [pendingRoles, setPendingRoles] = useState<StaffRole[]>(["content_manager"]);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [lastCreds, setLastCreds] = useState<{ email: string; password: string } | null>(null);

  useEffect(() => {
    if (perms.loading) return;
    if (!perms.canManageRoles) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [s, a] = await Promise.all([list(), audit({ data: { limit: 50 } })]);
        setStaff(s as Staff[]);
        setAuditRows(a as AuditRow[]);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [perms.loading, perms.canManageRoles, list, audit]);

  const sortedStaff = useMemo(
    () => [...staff].sort((a, b) => (a.email ?? "").localeCompare(b.email ?? "")),
    [staff],
  );

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || pendingRoles.length === 0 || password.length < 10) return;
    setBusy(true);
    try {
      await invite({ data: { email: email.trim(), password, roles: pendingRoles } });
      setLastCreds({ email: email.trim(), password });
      toast.success(isAr ? "تم إنشاء الحساب وإرسال البيانات" : "Account created & credentials emailed");
      setEmail("");
      setPassword(generatePassword());
      const s = await list();
      setStaff(s as Staff[]);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copyText(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${isAr ? "تم النسخ" : "copied"}`);
    } catch {
      toast.error("Copy failed");
    }
  }

  async function handleRolesChange(user_id: string, roles: StaffRole[]) {
    const target = staff.find((s) => s.user_id === user_id);
    const removingPrivilege =
      (target?.roles.includes("super_admin") || target?.roles.includes("admin")) &&
      !roles.some((r) => r === "super_admin" || r === "admin");
    const grantingSuper =
      !target?.roles.includes("super_admin") && roles.includes("super_admin");
    if (removingPrivilege || grantingSuper) {
      const msg = isAr
        ? "هذا تغيير حساس في الصلاحيات. هل أنت متأكد؟"
        : "This is a sensitive permission change. Are you sure?";
      if (!window.confirm(msg)) return;
    }
    try {
      await setRoles({ data: { user_id, roles } });
      toast.success(isAr ? "تم التحديث" : "Updated");
      setStaff((prev) => prev.map((s) => (s.user_id === user_id ? { ...s, roles } : s)));
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (perms.loading || loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  if (!perms.canManageRoles) {
    const currentEmail = user?.email ?? null;
    return (
      <div dir={dir} className="px-5 py-16 max-w-md mx-auto text-center">
        <div className="size-16 mx-auto rounded-full bg-clay/30 flex items-center justify-center mb-4">
          <Lock className="size-6 text-foreground/60" />
        </div>
        <h1 className="font-display-en text-2xl font-bold mb-2">
          {isAr ? "للمسؤول العام فقط" : "Super admins only"}
        </h1>
        <p className="text-sm text-foreground/70 mb-6">
          {isAr
            ? "هذه الصفحة تتطلب صلاحية مسؤول عام. سجّل الدخول بحساب الإدارة المخصّص."
            : "This page requires super admin access. Sign in with your admin email & password."}
          {currentEmail && (
            <span className="block mt-2 text-xs text-foreground/50">
              {isAr ? "حسابك الحالي: " : "Signed in as: "} <span className="font-mono">{currentEmail}</span>
            </span>
          )}
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <button
            onClick={async () => {
              await signOut();
              navigate({ to: "/login", search: { redirect: "/app/admin/team", context: "admin" } });
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
          >
            <LogIn className="size-4" />
            {isAr ? "تسجيل دخول الإدارة" : "Sign in as admin"}
          </button>
          <Link
            to="/app"
            className="px-5 py-2.5 rounded-2xl bg-clay/30 text-foreground font-bold text-sm"
          >
            {isAr ? "العودة" : "Go back"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div dir={dir} className="px-5 py-7 max-w-3xl mx-auto pb-12 space-y-8">
      <div><BackButton /></div>
      <div>
        <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-1">
          {isAr ? "الفريق" : "Team & Permissions"}
        </p>
        <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl font-bold tracking-tight`}>
          {isAr ? "أعضاء الفريق" : "Staff Members"}
        </h1>
        <p className="text-sm text-foreground/60 mt-1">
          {isAr
            ? "أنت تنشئ حسابات الفريق وكلمات المرور. تُرسل البيانات إلى بريد العضو مباشرة."
            : "You create each staff account & password. Credentials are emailed to them directly."}
        </p>
      </div>

      {/* Last credentials banner */}
      {lastCreds && (
        <div className="bg-sage/10 border-2 border-sage/40 rounded-3xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-sm text-sage">
              {isAr ? "تم إنشاء الحساب — احتفظ بالبيانات" : "Account ready — save these credentials"}
            </p>
            <button onClick={() => setLastCreds(null)} className="text-xs text-foreground/50 hover:text-foreground">×</button>
          </div>
          <div className="space-y-1 font-mono text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate"><strong>Email:</strong> {lastCreds.email}</span>
              <button onClick={() => copyText(lastCreds.email, "Email")} className="p-1 hover:bg-clay/30 rounded"><Copy className="size-3.5" /></button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate"><strong>Password:</strong> {lastCreds.password}</span>
              <button onClick={() => copyText(lastCreds.password, "Password")} className="p-1 hover:bg-clay/30 rounded"><Copy className="size-3.5" /></button>
            </div>
          </div>
          <p className="text-[11px] text-foreground/60">
            {isAr
              ? "تم إرسال نسخة إلى البريد. لن تظهر كلمة المرور مرة أخرى."
              : "A copy was emailed to them. The password will not be shown again."}
          </p>
        </div>
      )}

      {/* Invite */}
      <form
        onSubmit={handleInvite}
        className="bg-card border-2 border-clay/30 rounded-3xl p-4 space-y-3"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="size-4 text-primary" />
          <h2 className="font-bold text-sm">
            {isAr ? "إضافة عضو جديد" : "Add new staff member"}
          </h2>
        </div>
        <input
          type="email"
          required
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 rounded-2xl bg-background border-2 border-clay/30 focus:border-primary outline-none text-sm"
        />
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showPw ? "text" : "password"}
              required
              minLength={10}
              placeholder={isAr ? "كلمة المرور (10+ أحرف)" : "Password (10+ chars)"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 pr-10 rounded-2xl bg-background border-2 border-clay/30 focus:border-primary outline-none text-sm font-mono"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-foreground/50 hover:text-foreground"
              aria-label={showPw ? "Hide" : "Show"}
            >
              {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          <button
            type="button"
            onClick={() => { setPassword(generatePassword()); setShowPw(true); }}
            title={isAr ? "توليد كلمة مرور" : "Generate"}
            className="px-3 rounded-2xl bg-clay/30 hover:bg-clay/50 text-foreground/70"
          >
            <RefreshCw className="size-4" />
          </button>
        </div>
        <RolePicker
          isAr={isAr}
          value={pendingRoles}
          onChange={setPendingRoles}
          disableSelf={false}
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-60"
        >
          <UserPlus className="size-4" />
          {busy
            ? (isAr ? "جاري الإنشاء..." : "Creating...")
            : (isAr ? "إنشاء الحساب وإرسال البيانات" : "Create account & email credentials")}
        </button>
      </form>

      {/* Staff list */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="size-4 text-secondary" />
          <h2 className="font-bold text-sm">
            {isAr ? "الأعضاء الحاليون" : "Current staff"} ({sortedStaff.length})
          </h2>
        </div>
        {sortedStaff.map((s) => {
          const isSelf = s.user_id === user?.id;
          const isOwner = s.user_id === PLATFORM_OWNER_ID;
          const show = isSelf || revealed[s.user_id];
          const locked = isOwner && !isSelf;
          return (
          <div
            key={s.user_id}
            className={`bg-card border-2 rounded-3xl p-4 space-y-2 ${
              isOwner ? "border-primary/50" : "border-clay/30"
            }`}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <p className="font-bold text-sm truncate font-mono">
                  {show ? (s.email ?? s.user_id) : maskEmail(s.email)}
                </p>
                {isOwner && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-primary text-primary-foreground"
                    title={isAr ? "حساب المالك المحمي" : "Protected owner account"}
                  >
                    {isAr ? "المالك" : "Owner"}
                  </span>
                )}
                {!isSelf && (
                  <button
                    type="button"
                    onClick={() =>
                      setRevealed((r) => ({ ...r, [s.user_id]: !r[s.user_id] }))
                    }
                    className="p-1 rounded-md hover:bg-clay/30 text-foreground/50"
                    title={show ? "Hide" : "Reveal"}
                  >
                    {show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                )}
              </div>
              {s.last_sign_in && (
                <span className="text-[11px] text-foreground/50">
                  {isAr ? "آخر دخول: " : "Last seen: "}
                  {new Date(s.last_sign_in).toLocaleDateString()}
                </span>
              )}
            </div>
            {locked ? (
              <p className="text-[11px] text-foreground/60 italic">
                {isAr
                  ? "حساب المالك محمي ولا يمكن تعديل صلاحياته."
                  : "The owner account is protected and its permissions cannot be changed."}
              </p>
            ) : (
              <RolePicker
                isAr={isAr}
                value={s.roles}
                onChange={(roles) => handleRolesChange(s.user_id, roles)}
                disableSelf={false}
              />
            )}
          </div>
          );
        })}
      </div>

      {/* Audit log */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-accent" />
          <h2 className="font-bold text-sm">
            {isAr ? "سجل التغييرات" : "Audit log"} ({auditRows.length})
          </h2>
        </div>
        <div className="bg-card border-2 border-clay/30 rounded-3xl divide-y divide-clay/30">
          {auditRows.length === 0 ? (
            <p className="p-4 text-sm text-foreground/60 text-center">
              {isAr ? "لا توجد أحداث بعد" : "No events yet."}
            </p>
          ) : (
            auditRows.map((r) => (
              <div key={r.id} className="p-3 text-xs flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold truncate">{r.action}</p>
                  <p className="text-foreground/50 truncate">
                    {r.actor_email ?? r.actor_email ?? "—"} · {r.resource_type}
                    {r.resource_id ? `/${r.resource_id.slice(0, 8)}` : ""}
                  </p>
                </div>
                <span className="text-foreground/50 shrink-0">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function RolePicker({
  value,
  onChange,
  isAr,
}: {
  value: StaffRole[];
  onChange: (v: StaffRole[]) => void;
  isAr: boolean;
  disableSelf?: boolean;
}) {
  function toggle(r: StaffRole) {
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {ROLE_OPTIONS.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => toggle(opt.value)}
            title={opt.desc}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-clay/40 hover:border-primary/60 text-foreground/70"
            }`}
          >
            {isAr ? opt.ar : opt.en}
          </button>
        );
      })}
    </div>
  );
}
